import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Zap, RotateCcw, CircleCheck } from "lucide-react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const RESOURCE_LABELS = {
    cards: "credit cards",
    bank_accounts: "bank accounts",
    expenses: "expenses",
    statements: "statements",
    payments: "payments",
    assets: "assets",
    renewals: "renewals",
    life_events: "timeline events",
    subscriptions: "subscriptions",
    journeys: "journeys",
    journey_links: "journey links",
};

export default function DemoMode() {
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [confirmSeed, setConfirmSeed] = useState(false);
    const [confirmReset, setConfirmReset] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/demo/status");
            setStatus(data);
        } catch (err) {
            // silent — component just won't render below
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const seed = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/demo/seed");
            if (data.ok) {
                const n = Object.values(data.counts || {}).reduce((a, b) => a + b, 0);
                toast.success(`Demo Mode loaded · ${n} sample records ready.`);
            } else {
                toast.info(data.hint || "Demo data already present.");
            }
            setConfirmSeed(false);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const reset = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/demo/reset");
            toast.success(`Removed ${data.total} demo records.`);
            setConfirmReset(false);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    if (!status) return null;

    const present = status.present;
    const total = status.total || 0;
    const populated = Object.entries(status.counts || {}).filter(([, n]) => n > 0);

    return (
        <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50/60 to-fuchsia-50/40 p-5 mb-8" data-testid="demo-mode-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-violet-900 font-semibold">
                        <Sparkles size={11} /> Demo Mode
                    </div>
                    <div className="font-display text-2xl text-stone-900 leading-tight mt-1.5">
                        {present ? "Demo data is live" : "Try VAULT with sample data"}
                    </div>
                    <div className="text-[13px] text-stone-600 mt-1.5 leading-relaxed">
                        {present ? (
                            <>Your dashboard is showing {total} demo records marked as sample data. Reset any time — nothing you added yourself is affected.</>
                        ) : (
                            <>One click populates 5 credit cards, 2 journeys mid-flight, assets, renewals, statements, expenses, payments and subscriptions so you can explore every screen instantly.</>
                        )}
                    </div>
                    {present && populated.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                            {populated.map(([k, n]) => (
                                <span key={k} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white text-violet-800 border border-violet-200">
                                    {n} {RESOURCE_LABELS[k] || k}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {present ? (
                        <Button
                            onClick={() => setConfirmReset(true)}
                            variant="outline"
                            className="text-red-700 border-red-200 hover:bg-red-50"
                            data-testid="demo-reset-btn"
                        >
                            <RotateCcw size={13} className="mr-1.5" /> Reset demo
                        </Button>
                    ) : (
                        <Button
                            onClick={() => setConfirmSeed(true)}
                            className="bg-violet-700 hover:bg-violet-800 text-white"
                            data-testid="demo-seed-btn"
                        >
                            <Zap size={13} className="mr-1.5" /> Load demo data
                        </Button>
                    )}
                </div>
            </div>

            {/* Confirm seed */}
            <Dialog open={confirmSeed} onOpenChange={setConfirmSeed}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">Load demo data?</DialogTitle>
                        <DialogDescription className="text-[13px] text-stone-500">
                            34+ sample records will be inserted across cards, expenses, statements, payments,
                            journeys, assets, renewals and subscriptions. Every record is tagged so you can
                            reset with one click.
                        </DialogDescription>
                    </DialogHeader>
                    <ul className="text-[13px] text-stone-700 space-y-1 pl-1">
                        <li className="flex items-center gap-2"><CircleCheck size={13} className="text-emerald-600" /> Marked as sample — never mixes with your real data.</li>
                        <li className="flex items-center gap-2"><CircleCheck size={13} className="text-emerald-600" /> Idempotent — running twice does nothing.</li>
                        <li className="flex items-center gap-2"><CircleCheck size={13} className="text-emerald-600" /> Reset returns you to a clean slate.</li>
                    </ul>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmSeed(false)} disabled={busy}>Cancel</Button>
                        <Button onClick={seed} disabled={busy} className="bg-violet-700 hover:bg-violet-800 text-white" data-testid="demo-seed-confirm">
                            {busy ? "Loading…" : "Load sample data"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirm reset */}
            <Dialog open={confirmReset} onOpenChange={setConfirmReset}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">Reset demo data?</DialogTitle>
                        <DialogDescription className="text-[13px] text-stone-500">
                            Every sample record will be removed. Your real cards, expenses, statements and
                            journeys remain untouched.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmReset(false)} disabled={busy}>Cancel</Button>
                        <Button onClick={reset} disabled={busy} className="bg-red-700 hover:bg-red-800 text-white" data-testid="demo-reset-confirm">
                            {busy ? "Removing…" : "Remove sample data"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
